import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { SurveyType } from '../models/SurveySchema'
import { GetOrganizationFromPlanOrgId, GetPlanOrganizationsForAnnualPlan } from '../services/AnnualPlanService'
import { handleApiResponse } from '../utils/apiHelpers'
import { useAnnualPlanUserRole } from './use-annualPlanUserRole'
import { AnnualPlanRole } from '../models/AnnualPlanRole'

interface UseProgressReportAccessParams {
  annualPlanId: number
  planOrgId: number
  rcnId: number
  surveyTypeFromState?: SurveyType | null
  redirectPath: string
}

type AccessPermission = 'read' | 'edit'

interface UseProgressReportAccessReturn {
  isLoading: boolean
  surveyType: SurveyType | null
  userRole: AnnualPlanRole | null
  hasAccess: boolean
  permissions: AccessPermission[]
}

const validRoles = [AnnualPlanRole.PlanContributor, AnnualPlanRole.PlanAdministrator, AnnualPlanRole.PlanReviewer]

export const useProgressReportAccess = ({
  annualPlanId,
  planOrgId,
  rcnId,
  surveyTypeFromState,
  redirectPath
}: UseProgressReportAccessParams): UseProgressReportAccessReturn => {
  const navigate = useNavigate()
  const { userRole, isLoading: isLoadingRole } = useAnnualPlanUserRole(annualPlanId)

  const [surveyType, setSurveyType] = useState<SurveyType | null>(surveyTypeFromState ?? null)
  const [loadingSurveyType, setLoadingSurveyType] = useState(surveyTypeFromState == null)
  const [hasAccess, setHasAccess] = useState(false)
  const [accessChecked, setAccessChecked] = useState(false)
  const [permissions, setPermissions] = useState<AccessPermission[]>([])

  const determinePermissions = (userRole: AnnualPlanRole, isRcn: boolean): AccessPermission[] => {
    const perms: AccessPermission[] = []

    perms.push('read')
    if (userRole === AnnualPlanRole.PlanAdministrator) {
      perms.push('edit')
    }

    if (userRole === AnnualPlanRole.PlanContributor && !isRcn) {
      perms.push('edit')
    }

    return perms
  }

  const getAllPlanOrgIds = async (userRole: AnnualPlanRole): Promise<number[]> => {
    const allOrgs = await handleApiResponse(GetPlanOrganizationsForAnnualPlan(annualPlanId)) ?? []
    if (userRole === AnnualPlanRole.PlanReviewer || userRole === AnnualPlanRole.PlanAdministrator) {
      const rcnOrgResponse = await handleApiResponse(GetPlanOrganizationsForAnnualPlan(annualPlanId, true)) ?? []
      allOrgs.push(...rcnOrgResponse)
    }
    return allOrgs.map(org => org.id)
  }

  useEffect(() => {
    if (!isLoadingRole && (userRole == null || !validRoles.includes(userRole))) {
      navigate(redirectPath)
    }
  }, [isLoadingRole, userRole, navigate, redirectPath])

  useEffect(() => {
    const validateAccessAndDetermineSurveyType = async (): Promise<void> => {
      if (userRole == null) return
      if (accessChecked) return

      const planOrgIds = await getAllPlanOrgIds(userRole)
      if (surveyType != null) {
        if (!planOrgIds.some(id => id === planOrgId)) {
          navigate(redirectPath)
          setHasAccess(false)
          setPermissions([])
        } else {
          const organization = await GetOrganizationFromPlanOrgId(planOrgId)
          const isRcn = organization?.id === rcnId
          setHasAccess(true)
          setPermissions(determinePermissions(userRole, isRcn))
        }
        setLoadingSurveyType(false)
        setAccessChecked(true)
        return
      }

      if (planOrgIds == null || !planOrgIds.some(id => id === planOrgId)) {
        navigate(redirectPath)
        setHasAccess(false)
        setPermissions([])
        setLoadingSurveyType(false)
        setAccessChecked(true)
        return
      }

      const organization = await GetOrganizationFromPlanOrgId(planOrgId)
      const isRcn = organization?.id === rcnId
      const currentSurveyType = isRcn ? SurveyType.RCNMidYear : SurveyType.ISDMidYear
      setSurveyType(currentSurveyType)
      setHasAccess(true)
      setPermissions(determinePermissions(userRole, isRcn))
      setLoadingSurveyType(false)
      setAccessChecked(true)
    }

    void validateAccessAndDetermineSurveyType()
  }, [annualPlanId, planOrgId, rcnId, surveyType, navigate, redirectPath, accessChecked, userRole])

  const isLoading = isLoadingRole || loadingSurveyType || !accessChecked

  return {
    isLoading,
    surveyType,
    userRole,
    hasAccess,
    permissions
  }
}
